import * as React from "react"

import { useMotionPreference } from "../utils/motion-preference"

const DEFAULT_SELECTOR = ".wf-cta"

const FluidHoverEffect = ({
  targetSelector = DEFAULT_SELECTOR,
  canvasClassName = "wf-fluid",
  activeClassName = "is-fluid",
}) => {
  const canvasRef = React.useRef(null)
  const { reduceMotion } = useMotionPreference()

  React.useEffect(() => {
    if (reduceMotion) return undefined

    const canvas = canvasRef.current
    if (!canvas) return undefined

    const target = canvas.closest(targetSelector)
    if (!target) return undefined

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const gl = canvas.getContext("webgl", {
      alpha: true,
      premultipliedAlpha: false,
      antialias: false,
      depth: false,
      stencil: false,
    })

    if (!gl) return undefined

    const halfFloatExtension = gl.getExtension("OES_texture_half_float")
    gl.getExtension("OES_texture_half_float_linear")
    if (!halfFloatExtension) return undefined

    const halfFloat = halfFloatExtension.HALF_FLOAT_OES
    const filtering = gl.getExtension("OES_texture_half_float_linear") ? gl.LINEAR : gl.NEAREST
    const dpr = Math.min(window.devicePixelRatio || 1, 2)

    const sizeCanvas = () => {
      const bounds = target.getBoundingClientRect()
      const width = Math.max(2, Math.round(bounds.width * dpr))
      const height = Math.max(2, Math.round(bounds.height * dpr))
      const changed = canvas.width !== width || canvas.height !== height

      canvas.width = width
      canvas.height = height

      return { changed, height, width }
    }

    let aspect = 1

    const resolutionForAspect = base => (
      aspect >= 1
        ? { width: Math.round(base * aspect), height: base }
        : { width: base, height: Math.round(base / aspect) }
    )

    const compileShader = (type, source) => {
      const shader = gl.createShader(type)
      gl.shaderSource(shader, source)
      gl.compileShader(shader)

      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) || "Shader compile failed")
      }

      return shader
    }

    const baseVertexShader = `
      precision highp float;
      attribute vec2 aPosition;
      varying vec2 vUv;
      varying vec2 vL;
      varying vec2 vR;
      varying vec2 vT;
      varying vec2 vB;
      uniform vec2 texelSize;

      void main() {
        vUv = aPosition * 0.5 + 0.5;
        vL = vUv - vec2(texelSize.x, 0.0);
        vR = vUv + vec2(texelSize.x, 0.0);
        vT = vUv + vec2(0.0, texelSize.y);
        vB = vUv - vec2(0.0, texelSize.y);
        gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `

    const createProgram = fragmentShader => {
      const program = gl.createProgram()
      gl.attachShader(program, compileShader(gl.VERTEX_SHADER, baseVertexShader))
      gl.attachShader(program, compileShader(gl.FRAGMENT_SHADER, `precision highp float;
precision highp sampler2D;
varying vec2 vUv;
varying vec2 vL;
varying vec2 vR;
varying vec2 vT;
varying vec2 vB;
${fragmentShader}`))
      gl.bindAttribLocation(program, 0, "aPosition")
      gl.linkProgram(program)

      const uniforms = {}
      const uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS)
      for (let index = 0; index < uniformCount; index += 1) {
        const info = gl.getActiveUniform(program, index)
        uniforms[info.name] = gl.getUniformLocation(program, info.name)
      }

      return { program, uniforms }
    }

    const splatProgram = createProgram(`
      uniform sampler2D uTarget;
      uniform float aspectRatio;
      uniform float radius;
      uniform vec3 color;
      uniform vec2 point;

      void main() {
        vec2 p = vUv - point;
        p.x *= aspectRatio;
        vec3 splat = exp(-dot(p, p) / radius) * color;
        gl_FragColor = vec4(texture2D(uTarget, vUv).xyz + splat, 1.0);
      }
    `)

    const advectionProgram = createProgram(`
      uniform sampler2D uVelocity;
      uniform sampler2D uSource;
      uniform vec2 texelSize;
      uniform float dt;
      uniform float dissipation;

      void main() {
        vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize;
        gl_FragColor = texture2D(uSource, coord) / (1.0 + dissipation * dt);
      }
    `)

    const divergenceProgram = createProgram(`
      uniform sampler2D uVelocity;

      void main() {
        float left = texture2D(uVelocity, vL).x;
        float right = texture2D(uVelocity, vR).x;
        float top = texture2D(uVelocity, vT).y;
        float bottom = texture2D(uVelocity, vB).y;
        vec2 center = texture2D(uVelocity, vUv).xy;

        if (vL.x < 0.0) left = -center.x;
        if (vR.x > 1.0) right = -center.x;
        if (vT.y > 1.0) top = -center.y;
        if (vB.y < 0.0) bottom = -center.y;

        gl_FragColor = vec4(0.5 * (right - left + top - bottom), 0.0, 0.0, 1.0);
      }
    `)

    const curlProgram = createProgram(`
      uniform sampler2D uVelocity;

      void main() {
        float left = texture2D(uVelocity, vL).y;
        float right = texture2D(uVelocity, vR).y;
        float top = texture2D(uVelocity, vT).x;
        float bottom = texture2D(uVelocity, vB).x;
        gl_FragColor = vec4(0.5 * (right - left - (top - bottom)), 0.0, 0.0, 1.0);
      }
    `)

    const vorticityProgram = createProgram(`
      uniform sampler2D uVelocity;
      uniform sampler2D uCurl;
      uniform float curl;
      uniform float dt;

      void main() {
        float left = texture2D(uCurl, vL).x;
        float right = texture2D(uCurl, vR).x;
        float top = texture2D(uCurl, vT).x;
        float bottom = texture2D(uCurl, vB).x;
        float center = texture2D(uCurl, vUv).x;
        vec2 force = 0.5 * vec2(abs(top) - abs(bottom), abs(right) - abs(left));
        force /= length(force) + 0.0001;
        force *= curl * center;
        force.y *= -1.0;

        vec2 velocity = texture2D(uVelocity, vUv).xy + force * dt;
        gl_FragColor = vec4(clamp(velocity, -1000.0, 1000.0), 0.0, 1.0);
      }
    `)

    const pressureProgram = createProgram(`
      uniform sampler2D uPressure;
      uniform sampler2D uDivergence;

      void main() {
        float left = texture2D(uPressure, vL).x;
        float right = texture2D(uPressure, vR).x;
        float top = texture2D(uPressure, vT).x;
        float bottom = texture2D(uPressure, vB).x;
        float divergence = texture2D(uDivergence, vUv).x;
        gl_FragColor = vec4((left + right + bottom + top - divergence) * 0.25, 0.0, 0.0, 1.0);
      }
    `)

    const gradientProgram = createProgram(`
      uniform sampler2D uPressure;
      uniform sampler2D uVelocity;

      void main() {
        float left = texture2D(uPressure, vL).x;
        float right = texture2D(uPressure, vR).x;
        float top = texture2D(uPressure, vT).x;
        float bottom = texture2D(uPressure, vB).x;
        vec2 velocity = texture2D(uVelocity, vUv).xy - vec2(right - left, top - bottom);
        gl_FragColor = vec4(velocity, 0.0, 1.0);
      }
    `)

    const displayProgram = createProgram(`
      uniform sampler2D uTexture;

      void main() {
        vec3 color = texture2D(uTexture, vUv).rgb;
        float alpha = clamp(max(color.r, max(color.g, color.b)) * 1.6, 0.0, 1.0);
        gl_FragColor = vec4(color, alpha);
      }
    `)

    const quadBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)

    const blit = framebuffer => {
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer ? framebuffer.framebuffer : null)
      gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer)
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
      gl.enableVertexAttribArray(0)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    }

    const createFramebuffer = (width, height) => {
      const texture = gl.createTexture()
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filtering)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filtering)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, halfFloat, null)

      const framebuffer = gl.createFramebuffer()
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer)
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0)
      gl.viewport(0, 0, width, height)
      gl.clear(gl.COLOR_BUFFER_BIT)

      return {
        texture,
        framebuffer,
        width,
        height,
        texelX: 1 / width,
        texelY: 1 / height,
        attach(unit) {
          gl.activeTexture(gl.TEXTURE0 + unit)
          gl.bindTexture(gl.TEXTURE_2D, texture)
          return unit
        },
      }
    }

    const createDoubleFramebuffer = (width, height) => {
      let read = createFramebuffer(width, height)
      let write = createFramebuffer(width, height)

      return {
        width,
        height,
        texelX: 1 / width,
        texelY: 1 / height,
        get read() {
          return read
        },
        get write() {
          return write
        },
        swap() {
          const next = read
          read = write
          write = next
        },
      }
    }

    let velocity = null
    let dye = null
    let divergence = null
    let curl = null
    let pressure = null
    let lastPointer = null
    let isPointerInside = false

    const resetSimulation = () => {
      const { width, height } = sizeCanvas()
      aspect = width / height

      const velocityResolution = resolutionForAspect(112)
      const dyeResolution = resolutionForAspect(180)

      velocity = createDoubleFramebuffer(velocityResolution.width, velocityResolution.height)
      dye = createDoubleFramebuffer(dyeResolution.width, dyeResolution.height)
      divergence = createFramebuffer(velocityResolution.width, velocityResolution.height)
      curl = createFramebuffer(velocityResolution.width, velocityResolution.height)
      pressure = createDoubleFramebuffer(velocityResolution.width, velocityResolution.height)
      lastPointer = null
      isPointerInside = false
      target.classList.remove(activeClassName)

      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.viewport(0, 0, width, height)
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
    }

    resetSimulation()

    const splat = (x, y, dx, dy, color) => {
      if (!velocity || !dye) return
      gl.useProgram(splatProgram.program)
      gl.uniform1i(splatProgram.uniforms.uTarget, velocity.read.attach(0))
      gl.uniform1f(splatProgram.uniforms.aspectRatio, aspect)
      gl.uniform2f(splatProgram.uniforms.point, x, y)
      gl.uniform3f(splatProgram.uniforms.color, dx, dy, 0)
      gl.uniform1f(splatProgram.uniforms.radius, 0.022)
      gl.viewport(0, 0, velocity.width, velocity.height)
      blit(velocity.write)
      velocity.swap()

      gl.uniform1i(splatProgram.uniforms.uTarget, dye.read.attach(0))
      gl.uniform3f(splatProgram.uniforms.color, color[0], color[1], color[2])
      gl.viewport(0, 0, dye.width, dye.height)
      blit(dye.write)
      dye.swap()
    }

    const dyeColor = () => {
      const variance = 0.7 + Math.random() * 0.6
      const sparkle = Math.random() < 0.14 ? 0.45 : 0
      return [0.1 * variance + sparkle, 1 * variance, 0.5 * variance + sparkle]
    }

    const toUv = event => {
      const bounds = canvas.getBoundingClientRect()
      return {
        x: (event.clientX - bounds.left) / bounds.width,
        y: 1 - (event.clientY - bounds.top) / bounds.height,
      }
    }

    const handleMove = event => {
      if (sizeCanvas().changed) resetSimulation()
      const point = toUv(event)
      if (lastPointer) {
        const dx = (point.x - lastPointer.x) * 9
        const dy = (point.y - lastPointer.y) * 9
        if (dx * dx + dy * dy > 0) splat(point.x, point.y, dx, dy, dyeColor())
      }
      lastPointer = point
    }

    const handleEnter = event => {
      if (sizeCanvas().changed) resetSimulation()
      isPointerInside = true
      target.classList.add(activeClassName)
      const point = toUv(event)
      lastPointer = point
      splat(point.x, point.y, (Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2, dyeColor())
    }

    const handleLeave = () => {
      isPointerInside = false
      target.classList.remove(activeClassName)
      lastPointer = null
    }

    let frame = 0
    let disposed = false

    if (!prefersReducedMotion) {
      target.addEventListener("pointermove", handleMove)
      target.addEventListener("pointerenter", handleEnter)
      target.addEventListener("pointerleave", handleLeave)
    }

    const resizeObserver = new ResizeObserver(() => {
      if (disposed) return

      const bounds = target.getBoundingClientRect()
      if (bounds.width < 4 || bounds.height < 4) return

      resetSimulation()
    })
    resizeObserver.observe(target)

    const dt = 0.016
    const velocityDissipationActive = 0.5
    const velocityDissipationIdle = 3.2
    const dyeDissipationActive = 0.4
    const dyeDissipationIdle = 4.8
    const curlStrength = 30
    const pressureIterations = 18

    const step = () => {
      if (!velocity || !dye || !divergence || !curl || !pressure) {
        if (!disposed) frame = requestAnimationFrame(step)
        return
      }

      const texelX = velocity.texelX
      const texelY = velocity.texelY
      gl.disable(gl.BLEND)

      gl.useProgram(curlProgram.program)
      gl.uniform2f(curlProgram.uniforms.texelSize, texelX, texelY)
      gl.uniform1i(curlProgram.uniforms.uVelocity, velocity.read.attach(0))
      gl.viewport(0, 0, velocity.width, velocity.height)
      blit(curl)

      gl.useProgram(vorticityProgram.program)
      gl.uniform2f(vorticityProgram.uniforms.texelSize, texelX, texelY)
      gl.uniform1i(vorticityProgram.uniforms.uVelocity, velocity.read.attach(0))
      gl.uniform1i(vorticityProgram.uniforms.uCurl, curl.attach(1))
      gl.uniform1f(vorticityProgram.uniforms.curl, curlStrength)
      gl.uniform1f(vorticityProgram.uniforms.dt, dt)
      blit(velocity.write)
      velocity.swap()

      gl.useProgram(divergenceProgram.program)
      gl.uniform2f(divergenceProgram.uniforms.texelSize, texelX, texelY)
      gl.uniform1i(divergenceProgram.uniforms.uVelocity, velocity.read.attach(0))
      blit(divergence)

      gl.useProgram(pressureProgram.program)
      gl.uniform2f(pressureProgram.uniforms.texelSize, texelX, texelY)
      gl.uniform1i(pressureProgram.uniforms.uDivergence, divergence.attach(0))
      for (let iteration = 0; iteration < pressureIterations; iteration += 1) {
        gl.uniform1i(pressureProgram.uniforms.uPressure, pressure.read.attach(1))
        blit(pressure.write)
        pressure.swap()
      }

      gl.useProgram(gradientProgram.program)
      gl.uniform2f(gradientProgram.uniforms.texelSize, texelX, texelY)
      gl.uniform1i(gradientProgram.uniforms.uPressure, pressure.read.attach(0))
      gl.uniform1i(gradientProgram.uniforms.uVelocity, velocity.read.attach(1))
      blit(velocity.write)
      velocity.swap()

      gl.useProgram(advectionProgram.program)
      const velocityDissipation = isPointerInside ? velocityDissipationActive : velocityDissipationIdle
      const dyeDissipation = isPointerInside ? dyeDissipationActive : dyeDissipationIdle
      gl.uniform2f(advectionProgram.uniforms.texelSize, texelX, texelY)
      gl.uniform1i(advectionProgram.uniforms.uVelocity, velocity.read.attach(0))
      gl.uniform1i(advectionProgram.uniforms.uSource, velocity.read.attach(0))
      gl.uniform1f(advectionProgram.uniforms.dt, dt)
      gl.uniform1f(advectionProgram.uniforms.dissipation, velocityDissipation)
      gl.viewport(0, 0, velocity.width, velocity.height)
      blit(velocity.write)
      velocity.swap()

      gl.uniform1i(advectionProgram.uniforms.uVelocity, velocity.read.attach(0))
      gl.uniform1i(advectionProgram.uniforms.uSource, dye.read.attach(1))
      gl.uniform1f(advectionProgram.uniforms.dissipation, dyeDissipation)
      gl.viewport(0, 0, dye.width, dye.height)
      blit(dye.write)
      dye.swap()

      gl.useProgram(displayProgram.program)
      gl.uniform1i(displayProgram.uniforms.uTexture, dye.read.attach(0))
      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      blit(null)

      if (!disposed) frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)

    return () => {
      disposed = true
      cancelAnimationFrame(frame)
      target.removeEventListener("pointermove", handleMove)
      target.removeEventListener("pointerenter", handleEnter)
      target.removeEventListener("pointerleave", handleLeave)
      resizeObserver.disconnect()
      target.classList.remove(activeClassName)
    }
  }, [activeClassName, reduceMotion, targetSelector])

  if (reduceMotion) return null

  return <canvas className={canvasClassName} ref={canvasRef} aria-hidden="true" />
}

export default FluidHoverEffect
